import styled from 'styled-components';
import { gridConfig } from '../../fuse-responsive-grid/src/grid-config';
import { TypographyComponent } from '../../fuse-typography-element/src/typography';
import cb1VariantConfig from './cb1-variant-config';

const StyledContentBanner = styled.div
	.withConfig({
		shouldForwardProp: (prop) =>
			!['addBrandColour', 'lLayout', 'smallVariant', 'variant'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1',
	})`
	border: none;
	display: grid;
	gap: var(--spacing-global-space5);
	position: relative;
	padding: var(--spacing-global-space6);
	text-align: left;
	text-decoration: none;

	${(props) =>
		props.as === 'button'
			? `
		cursor: pointer;
	`
			: ``}

	background: ${({ addBrandColour, smallVariant, variant }) =>
		addBrandColour && smallVariant
			? cb1VariantConfig[variant].background.smallSize
			: cb1VariantConfig[variant].background.default};
	box-sizing: border-box;
	box-shadow: inset 0 0 0 var(--border-width-l) ${({ variant }) => cb1VariantConfig[variant].border};

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		grid-template-columns: ${(props) => (props.image ? 'max-content 1fr' : '1fr')};
		gap: var(--spacing-global-space6);
		align-items: start;
	}

	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		padding: var(--spacing-global-space6) var(--spacing-global-space7);
		align-items: ${({ lLayout }) => (lLayout === 'v1' ? 'normal' : 'start')};
	}

	&:focus {
		outline: var(--border-radius-s) solid var(--color-functional-focus);
		outline-offset: -2px;
	}

	&:active {
		.fuse-content-banner-1__primary-span-cta {
			box-shadow: inset 0 0 0 var(--border-radius-s) var(--color-global-white);
			color: var(--color-brand-mid);
			background-color: var(--color-global-white);
		}
		.fuse-content-banner-1__primary-span-cta:before {
			background: var(--color-brand-mid);
		}
	}

	&:hover {
		.fuse-content-banner-1__primary-span-cta {
			border: 1px solid var(--color-brand-mid);
			box-shadow: var(--elevation-shadow-s);
		}
	}
`;

const StyledPicture = styled.div
	.withConfig({
		shouldForwardProp: (prop) => !['imageSize', 'lLayout'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__picture',
		'data-testid': 'fuse-content-banner-1-picture-test-id',
	})`
	display: grid;
	position: relative;
	align-self: start;
	width: ${({ imageSize }) => (imageSize === 'small' ? 'calc(7.5 * 1.6rem)' : '100%')};
	aspect-ratio: ${({ imageSize }) => (imageSize === 'small' ? '4/3' : '16/9')};

	@media screen and (min-width: ${gridConfig.m.minWidth}) {
		width: calc(${({ imageSize }) => (imageSize === 'small' ? '8.5' : '16.9')} * 1.6rem);
	}

	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		width: calc(
			${({ imageSize, lLayout }) =>
		imageSize === 'small' ? `${lLayout === 'v1' ? '10' : '8.5'}` : '16.9'} * 1.6rem
		);
	}

	// Required styles for the smallVariant prop - avoids more conditions on each individual section
	${({ smallVariant }) =>
		smallVariant &&
		`
			&& {
				@media screen and (max-width: ${gridConfig.m.maxWidth}) {
					width: calc(7.5 * 1.6rem);
					aspect-ratio: 4/3;
				}
			}
		`}

	.fuse-picture {
		display: grid;
	}

	.fuse-picture__img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const StyledContent = styled.div
	.withConfig({
		shouldForwardProp: (prop) => !['lLayout'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__content',
		'data-testid': 'fuse-content-banner-1-content-test-id',
	})`
	display: grid;
	gap: var(--spacing-global-space6);

	${({ lLayout }) =>
		lLayout === 'v1'
			? `
	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		grid-template-columns: 1fr max-content;
		gap: var(--spacing-global-space9);
	}
	`
			: ''}
`;

const StyledText = styled.div.attrs({
	className: 'fuse-content-banner-1__text',
	'data-testid': 'fuse-content-banner-1-text-test-id',
})`
	display: grid;
	align-self: start;
	gap: var(--spacing-global-space2);
`;

const StyledHeading = styled(TypographyComponent)
	.withConfig({
		shouldForwardProp: (prop) => !['addBrandColour', 'smallVariant', 'variant'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__heading',
		'data-testid': 'fuse-content-banner-1-heading-test-id',
	})`
	&& {
		color:  ${({ addBrandColour, smallVariant, variant }) => {
		if (addBrandColour === 'heading') {
			if (smallVariant) {
				return cb1VariantConfig[variant].addBrandText.smallSize;
			}
			return cb1VariantConfig[variant].addBrandText.default;
		}
		return cb1VariantConfig[variant].baseText.headings;
	}};
`;

const StyledSubHeading = styled(TypographyComponent)
	.withConfig({
		shouldForwardProp: (prop) => !['addBrandColour', 'smallVariant', 'variant'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__sub-heading',
		'data-testid': 'fuse-content-banner-1-sub-heading-test-id',
	})`
		&& {
			color:  ${({ addBrandColour, smallVariant, variant }) => {
		if (addBrandColour === 'subHeading') {
			if (smallVariant) {
				return cb1VariantConfig[variant].addBrandText.smallSize;
			}
			return cb1VariantConfig[variant].addBrandText.default;
		}
		return cb1VariantConfig[variant].baseText.headings;
	}};
`;

const StyledParagraph = styled(TypographyComponent)
	.withConfig({
		shouldForwardProp: (prop) => !['variant'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__paragraph',
		'data-testid': 'fuse-content-banner-1-paragraph-test-id',
	})`
	&& {
		color: ${({ variant }) => cb1VariantConfig[variant].baseText.paragraphs};

		margin-bottom: 0;
	}
`;

const StyledRepresentative = styled(TypographyComponent)
	.withConfig({
		shouldForwardProp: (prop) => !['addBrandColour', 'smallVariant', 'variant'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__representative-text',
		'data-testid': 'fuse-content-banner-1-representative-text-test-id',
	})`
&& {
	color:  ${({ addBrandColour, smallVariant, variant }) => {
		if (addBrandColour === 'representative') {
			if (smallVariant) {
				return cb1VariantConfig[variant].addBrandText.smallSize;
			}
			return cb1VariantConfig[variant].addBrandText.default;
		}
		return cb1VariantConfig[variant].baseText.headings;
	}};

	span.fuse-content-banner-1__wrap-apr {
	display: inline-flex;
	}
`;

const StyledButtons = styled.div
	.withConfig({
		shouldForwardProp: (prop) => !['lLayout'].includes(prop),
	})
	.attrs({
		className: 'fuse-content-banner-1__buttons',
		'data-testid': 'fuse-content-banner-1-buttons-test-id',
	})`
	display: flex;
	flex-wrap: wrap;
	gap: var(--spacing-global-space5);
	align-self: center;

	@media screen and (min-width: ${gridConfig.l.minWidth}) {
		flex-direction: ${({ lLayout }) => (lLayout === 'v1' ? 'column' : 'row')};
	}

	&.fuse-content-banner-1__buttons--stacked {
		flex-direction: column;
		width: fit-content;
	}
	.fuse-content-banner-1__primary-cta--stacked,
	.fuse-content-banner-1__secondary-cta--stacked {
		width: 100%;
	}
`;

export {
	StyledContentBanner,
	StyledPicture,
	StyledContent,
	StyledText,
	StyledHeading,
	StyledSubHeading,
	StyledParagraph,
	StyledRepresentative,
	StyledButtons,
};
